import React, { useEffect, useState, useMemo } from 'react';
import * as TabilityTypes from 'types';
import * as workspaceUtils from 'utils/workspaceUtils';
import styled from 'styled-components';
import theme from 'theme';

// Components
import { useSelector, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import queryKeys from 'config/queryKeys';
import { fetchPlanDetails, fetchPlans } from 'api/remote';
import { ReactFlowProvider } from 'reactflow';
import UpgradeRequired from './UpgradeRequired';
import KoalaButton from 'koala/components/Button';
import { useLocation, useHistory } from 'react-router-dom';
import * as routes from 'routes';
import ShareIconButton from 'components/ShareIconButton';
import { PlanMap, MapNode } from 'components/MapComponents';
import { Base64 } from 'js-base64';

const Container = styled.div`
  width: 100%;
  height: 50vh;
  position: relative;
`;

const Mask = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  background: rgba(255, 255, 255, 0.6);
`;

const UpgradeModal = styled.div`
  position: absolute;
  left: 50%;
  max-height: 25rem;
  z-index: 10;
  box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  overflow: hidden;
  height: 25rem;
  top: 50%;
  width: 60rem;
  max-width: 60rem;
  margin-left: -30rem;
  margin-top: -12rem;
  background: #fff;

  @media ${theme.devices.mobile} {
    width: 100%;
    margin: 20rem 0 0 0;
    margin-top: -20rem;
    left: unset;
    overflow: auto;
    height: 30rem;
    max-height: 30rem;
  }
`;

const HeaderContainer = styled.div`
  padding: ${theme.spacing.x1} ${theme.spacing.x2};
  border-bottom: 1px solid ${theme.colors.N10};
  display: flex;
  justify-content: space-between;
  align-items: center;

  .title {
    font-size: 1rem;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .actions {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.x1};
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: ${theme.colors.N0};
  border: 1px solid ${theme.colors.blockBorder};
  border-radius: 4px;
  &.full-screen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 20;
  }
`;
interface Props {
  rootPlan: TabilityTypes.Plan;
}

function PlanMinimap(props: Props) {
  const { rootPlan } = props;
  const history = useHistory();
  const location = useLocation();
  const currentWorkspace: TabilityTypes.Workspace = useSelector(
    (state: any) => state.session.currentWorkspace,
    shallowEqual,
  );
  const { slug } = currentWorkspace;
  const { t } = useTranslation();

  const [rootId, setRootId] = useState(rootPlan.id);
  const [hasFetchedChildPlans, setHasFetchedChildPlans] = useState(false);
  const [hasFetchedParent, setHasFetchedParent] = useState(false);
  const [mapState, setMapState] = useState<{ [key: string]: any }>({});

  const searchParams = new URLSearchParams(location.search);
  const mapStateHash = searchParams.get('mapState');
  useEffect(() => {
    if (mapStateHash) {
      try {
        setMapState(JSON.parse(Base64.decode(mapStateHash)));
      } catch (e) {}
    }
    // } else {
    //   const encodedHash = Base64.encode(JSON.stringify({ rootId: true }));
    //   history.push(location.pathname + '?mapState=' + encodedHash);
    // }
  }, [location.pathname, mapStateHash, history]);

  const initialNodes = useMemo(
    () => ({
      [rootPlan.id]: {
        id: rootPlan.id,
        meta: {
          expanded: true,
          plan: rootPlan,
          showOutcomes: false,
          isFocus: true,
          childPlans: [],
          getAllChildren: true,
          hideExpandable: true,
        },
        type: 'plan',
        name: rootPlan.title,
        children: [],
        parents: [],
      },
      [`${rootPlan.id}-add`]: {
        id: `${rootPlan.id}-add`,
        meta: { plan: rootPlan },
        type: 'addChild',
        name: `${rootPlan.id}-add`,
        children: [],
        parents: [rootPlan.id],
      },
    }),
    [rootPlan],
  );

  const [initialTree, setInitialTree] = useState<Record<string, MapNode>>(initialNodes);
  const fullScreenEnabled = useLocation().pathname.includes('minimap');
  const hasParent = !!rootPlan.parent_id;
  const hasPremiumSubscription = workspaceUtils.hasPremiumSubscription(currentWorkspace);

  useEffect(() => {
    setRootId(rootPlan.id);
    setHasFetchedChildPlans(false);
    setHasFetchedParent(false);
    setInitialTree(initialNodes);
  }, [initialNodes, rootPlan]);

  // Check the upgrade required flag
  let upgradeRequired = !hasPremiumSubscription;

  // get child plans
  const params = useMemo(
    () => ({
      limit: 200,
      filter: {
        children_of: rootPlan.id,
        status: '',
        done: false,
      },
    }),
    [rootPlan.id],
  );
  useQuery([queryKeys.plans, slug, params], fetchPlans, {
    onSuccess: (response) => {
      // Create the plans mapping
      const plans: TabilityTypes.Plan[] = response.data;
      const newTree = plans.reduce(
        (acc, plan) => {
          const expandedState = mapState['expandedPlans'] ? mapState['expandedPlans'][plan.id] : true;
          const children = [...(acc[rootPlan.id].children || []), `${plan.id}`];
          const result = {
            ...acc,
            [rootPlan.id]: {
              ...acc[rootPlan.id],
              children,
            },
            [plan.id]: {
              id: plan.id,
              meta: { expanded: expandedState, plan, showOutcomes: false, getAllChildren: true },
              type: 'plan',
              name: plan.title,
              children: [],
              parents: [rootPlan.id],
            },
          };
          return result;
        },
        {
          ...initialTree,
        },
      );
      newTree[rootPlan.id].children = [...newTree[rootPlan.id].children, `${rootPlan.id}-add`];
      setInitialTree(newTree);
      setHasFetchedChildPlans(true);
    },
  });

  const parentQueryKey = [queryKeys.currentPlan, rootPlan.parent_id];
  useQuery(parentQueryKey, fetchPlanDetails, {
    enabled: hasParent,
    onSuccess: (response) => {
      const parent: TabilityTypes.Plan = response.data;
      if (parent) {
        const parentNode = {
          id: parent.id,
          meta: { expanded: true, plan: parent, showOutcomes: false, hideExpandable: true, getAllChildren: true },
          type: 'plan',
          name: parent.title,
          children: [rootPlan.id],
          parents: [],
        };

        setInitialTree((prev) => {
          return { ...prev, [parent.id]: parentNode };
        });
        setRootId(parent.id);
      }

      setHasFetchedParent(true);
    },
  });

  const fullScreenClass = fullScreenEnabled ? 'full-screen' : '';

  const minimapPath = routes.WORKSPACE_PLAN_TRACK_MINIMAP_ROUTE.replace(':workspaceSlug', slug).replace(
    ':planId',
    rootPlan.nano_slug,
  );
  const planPath = routes.WORKSPACE_PLAN_TRACK_ROUTE.replace(':workspaceSlug', slug).replace(
    ':planId',
    rootPlan.nano_slug,
  );

  const toggleFullScreen = () => {
    if (fullScreenEnabled) {
      history.push(planPath);
    } else {
      history.push(minimapPath);
    }
  };
  return (
    <Container>
      {upgradeRequired && (
        <>
          <Mask />
          <UpgradeModal>
            <UpgradeRequired />
          </UpgradeModal>
        </>
      )}
      <Content className={fullScreenClass}>
        <HeaderContainer>
          <span className="title">Minimap</span>
          <div className="actions">
            <ShareIconButton
              id={rootPlan.id}
              link={`https://${process.env.REACT_APP_DOMAIN}${minimapPath}`}
              edge="square"
              size="small"
            />
            <KoalaButton onClick={toggleFullScreen} appearance="secondary" size="small">
              {fullScreenEnabled ? t('shared.exitFullScreen') : t('shared.enterFullScreen')}
            </KoalaButton>
          </div>
        </HeaderContainer>
        {hasFetchedChildPlans && (!hasParent || hasFetchedParent) && (
          <ReactFlowProvider>
            <PlanMap initialTree={initialTree} rootId={rootId} getAllChildren={true} />
          </ReactFlowProvider>
        )}
      </Content>
    </Container>
  );
}

export default PlanMinimap;
